import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {
  getComments,
  getCommentSnippet,
  getLatestSubmittedReply,
  getPendingComments,
  getSubmittedComments,
  isPendingComment,
} from '../util/thread-comment-accessors'
import styles from './CompactThread.module.css'
import {DotFillIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'

function PendingCommentIcon() {
  return (
    <span className={styles.pendingIconLabel}>
      <DotFillIcon size={12} />
      <span className="sr-only">Pending: </span>
    </span>
  )
}

function CompactComment({
  comment,
  emphasized = false,
}: {
  comment: ReturnType<typeof getComments>[number]
  emphasized?: boolean
}) {
  const login = comment.author?.login ?? 'ghost'
  const isPending = isPendingComment(comment)
  return (
    <div className={styles.commentRow}>
      {isPending ? (
        <PendingCommentIcon />
      ) : (
        <GitHubAvatar src={comment.author?.avatarUrl ?? ''} size={16} alt="" className={styles.avatar} />
      )}
      <span className={emphasized ? styles.authorEmphasized : styles.author}>{login}</span>
      <span className={clsx(styles.snippet, isPending && styles.pending)}>{getCommentSnippet(comment)}</span>
    </div>
  )
}

/**
 * A condensed, always-visible preview of a comment thread shown below the
 * collapsed ForYouItem header. The file name and lines are surfaced in the
 * header; this preview shows the initial comment, a "N more replies" divider
 * for any middle comments, and the latest reply. Clicking the parent item
 * expands to the full thread interface (which shows the full file path).
 */
export function CompactThread({thread}: {thread: Thread}) {
  const comments = getSubmittedComments(thread)
  const initial = comments[0]
  if (!initial) return null

  const latestReply = getLatestSubmittedReply(thread)
  const hiddenReplyCount = Math.max(comments.length - 2, 0)

  const pendingComments = getPendingComments(thread)

  return (
    <div className={styles.container}>
      <div className={styles.comments}>
        <CompactComment comment={initial} />
        {hiddenReplyCount > 0 && (
          <div className={styles.moreReplies}>
            <span>{`${hiddenReplyCount} more ${hiddenReplyCount === 1 ? 'reply' : 'replies'}`}</span>
          </div>
        )}
        {latestReply && <CompactComment comment={latestReply} emphasized />}
        {pendingComments.map(comment => (
          <CompactComment comment={comment} key={comment.id} />
        ))}
      </div>
    </div>
  )
}
